// Align Panel Component - Handles iterative sketch improvement
// Organized for Supabase Migration

import React, { useState } from 'react';
import { Download, CheckCircle, RotateCcw } from 'lucide-react';

export const AlignPanel = ({ 
  currentSketch, 
  getCurrentParams, 
  getSketchPoints,
  onAlignComplete,
  viewportWidth,
  viewportHeight,
  viewportScale
}) => {
  const [alignMode, setAlignMode] = useState(false);
  const [originalParams, setOriginalParams] = useState(null);
  const [alignmentHistory, setAlignmentHistory] = useState([]);
  const [showAlignmentGuide, setShowAlignmentGuide] = useState(false);

  const handleStartAlignment = () => {
    if (!currentSketch) return;
    
    const currentParams = getCurrentParams();
    if (currentParams) {
      setOriginalParams(JSON.parse(JSON.stringify(currentParams)));
      setAlignMode(true);
      setShowAlignmentGuide(true);
    }
  };

  const handleCompleteAlignment = () => {
    if (!alignMode || !originalParams) return;
    
    const currentParams = getCurrentParams();
    if (currentParams) {
      const alignmentData = {
        id: Date.now(),
        sketchId: currentSketch.id,
        sketchName: currentSketch.name,
        timestamp: new Date().toISOString(),
        originalParams: originalParams,
        alignedParams: currentParams,
        changes: calculateChanges(originalParams, currentParams),
        viewport: { width: viewportWidth, height: viewportHeight, scale: viewportScale }
      };

      setAlignmentHistory(prev => [alignmentData, ...prev]);
      
      if (onAlignComplete) {
        onAlignComplete(alignmentData);
      }

      setAlignMode(false);
      setShowAlignmentGuide(false);
      setOriginalParams(null);
    }
  };

  const handleResetAlignment = () => {
    if (originalParams && onAlignComplete) {
      onAlignComplete(originalParams);
    }
    setAlignMode(false);
    setShowAlignmentGuide(false);
    setOriginalParams(null);
  };

  const calculateChanges = (original, aligned) => {
    const changes = {};
    Object.keys(aligned).forEach(key => {
      if (typeof aligned[key] === 'number' && typeof original[key] === 'number') {
        const diff = aligned[key] - original[key];
        if (Math.abs(diff) > 0.1) {
          changes[key] = {
            from: original[key],
            to: aligned[key],
            difference: diff
          };
        }
      }
    });
    return changes;
  };

  const exportAlignmentData = () => {
    if (!alignMode || !originalParams) return;
    
    const currentParams = getCurrentParams();
    if (!currentParams) return;

    const changes = calculateChanges(originalParams, currentParams);
    const changeCount = Object.keys(changes).length;

    const alignmentData = {
      alignment_record: {
        id: `align-${Date.now()}`,
        sketch_id: currentSketch.id,
        sketch_name: currentSketch.name,
        alignment_type: "user_feedback",
        status: "completed",
        original_params: originalParams,
        aligned_params: currentParams,
        changes_summary: changes,
        change_count: changeCount,
        viewport_settings: { 
          width: viewportWidth, 
          height: viewportHeight, 
          scale: viewportScale 
        },
        alignment_notes: `User aligned ${changeCount} parameter(s) for better visualization`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      implementation_instructions: {
        message: "Please review the aligned parameters and update the sketch's default_params if the alignment improves visibility",
        steps: [
          "1. Review the changes_summary to see what parameters were adjusted",
          "2. Test the aligned parameters in different viewport sizes",
          "3. If alignment is better, update the sketch's default_params in the data file",
          "4. Consider if the alignment should be applied to all similar sketches"
        ],
        affected_file: currentSketch.category_id === '3d-geometry' 
          ? 'src/data/advanced3DGeometryData.js' 
          : 'src/data/sketchesData.js',
        sketch_id: currentSketch.id
      },
      metadata: {
        app_version: "3.0",
        export_date: new Date().toISOString(),
        user_agent: navigator.userAgent
      }
    };

    const dataStr = JSON.stringify(alignmentData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `alignment_${currentSketch.id}_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    alert(`Alignment data exported!\n\nChanged ${changeCount} parameter(s).\nPlease share this file for implementation feedback.`);
  };

  const renderAlignmentGuide = () => {
    if (!showAlignmentGuide) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-lg mx-4">
          <div className="flex items-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Alignment Mode Active</h3>
          </div>
          
          <div className="space-y-4 text-sm text-gray-600">
            <p>🎯 <strong>Current Mode:</strong> Sketch Alignment</p>
            <p>📐 <strong>What to do:</strong></p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the <strong>Pan Control</strong> to move the entire sketch away from boundaries</li>
              <li>Drag the <strong>red points</strong> directly on the sketch to align them precisely</li>
              <li>Take your time to get the alignment perfect</li>
              <li>When satisfied, click <strong>"Complete Alignment"</strong></li>
            </ul>
            <p>💡 <strong>Tip:</strong> You can export the aligned parameters for implementation feedback</p>
          </div>
          
          <div className="flex gap-2 mt-6">
            <button
              onClick={handleCompleteAlignment}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
            >
              Complete Alignment
            </button>
            <button
              onClick={handleResetAlignment}
              className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!currentSketch) return null;

  return (
    <div className="bg-green-50 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-green-800 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          Sketch Alignment
        </h4>
        {alignMode && (
          <span className="px-2 py-1 text-xs bg-green-600 text-white rounded-full">
            Active
          </span>
        )}
      </div>

      {!alignMode ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Use this tool to align your sketch and export the improved parameters for implementation feedback.
          </p>
          
          <button
            onClick={handleStartAlignment}
            className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors flex items-center justify-center"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Start Alignment Process
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-green-100 rounded p-3 text-sm text-green-800">
            <p className="font-medium mb-1">Alignment Mode Active</p>
            <p>• Use Pan Control to move sketch away from boundaries</p>
            <p>• Drag red points to align them precisely</p>
            <p>• When satisfied, complete the alignment</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={exportAlignmentData}
              className="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Aligned Data
            </button>
            
            <button
              onClick={handleCompleteAlignment}
              className="flex-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 transition-colors flex items-center justify-center"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete Alignment
            </button>
          </div>

          <button
            onClick={handleResetAlignment}
            className="w-full bg-gray-600 text-white px-3 py-2 rounded hover:bg-gray-700 transition-colors flex items-center justify-center"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Original
          </button>
        </div>
      )}

      {/* Alignment History */}
      {alignmentHistory.length > 0 && (
        <div className="mt-4 pt-4 border-t border-green-200">
          <h5 className="text-sm font-medium text-green-800 mb-2">Recent Alignments</h5>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {alignmentHistory.slice(0, 3).map((record) => (
              <div key={record.id} className="bg-white rounded p-2 text-xs">
                <div className="font-medium">{record.sketchName}</div>
                <div className="text-gray-500">
                  {new Date(record.timestamp).toLocaleTimeString()} - 
                  {Object.keys(record.changes).length} changes
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {renderAlignmentGuide()}
    </div>
  );
};
